import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchArticles, migrateNewsToSupabase, type NewsArticle } from '@/utils/adminStore';
import ScrollReveal from '@/components/base/ScrollReveal';

const categoryLabels: Record<string, string> = {
  brand: '品牌新闻',
  training: '培训赋能',
  store: '门店动态',
  franchise: '招商资讯',
  health: '健康知识',
};

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      await migrateNewsToSupabase();
      const data = await fetchArticles();
      const found = data.find((a) => a.id === Number(id));
      if (found) {
        setArticle(found);
      } else {
        setLoadError('not_found');
      }
    } catch {
      setLoadError('加载文章失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <main className="min-h-screen bg-brand-gray flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold mb-3" />
          <p className="text-sm text-gray-400">正在加载文章...</p>
        </div>
      </main>
    );
  }

  if (loadError === 'not_found' || !article) {
    return (
      <main className="min-h-screen bg-brand-gray flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-white text-brand-brown">
            <i className="ri-file-unknow-line text-2xl" />
          </div>
          <h2 className="text-lg font-semibold text-brand-deep mb-2">文章未找到</h2>
          <p className="text-gray-400 text-sm mb-6">该资讯可能已被移除或链接失效</p>
          <button
            onClick={() => navigate('/news')}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-deep text-white text-sm font-medium rounded-full hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回品牌动态
          </button>
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-screen bg-brand-gray flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-deep text-white text-sm font-medium rounded-full hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重试
          </button>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="relative h-[300px] md:h-[380px] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/60" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-10">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal variant="fade-up">
              <span className="inline-block text-xs font-medium text-white/80 bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full mb-4">
                {categoryLabels[article.category] || article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3">
                {article.title}
              </h1>
              <p className="text-white/50 text-sm flex items-center gap-1">
                <i className="ri-calendar-line text-xs" />
                {article.date}
              </p>
            </ScrollReveal>
          </div>
        </div>
      </div>

      <section className="bg-brand-gray py-10 md:py-14">
        <div className="px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal variant="fade-up" delay={100}>
              <button
                onClick={() => navigate('/news')}
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-brand-brown transition-colors mb-8 cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                返回品牌动态
              </button>
            </ScrollReveal>

            <ScrollReveal variant="fade-up" delay={150}>
              <article className="bg-white rounded-2xl p-6 md:p-10 shadow-sm">
                <div className="max-w-none">
                  {article.content_html ? (
                    <div
                      className="prose prose-sm md:prose-base max-w-none
                        prose-headings:text-brand-deep prose-headings:font-semibold
                        prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base
                        prose-img:rounded-xl prose-img:max-w-full prose-img:h-auto prose-img:my-6
                        prose-a:text-brand-brown prose-a:underline
                        prose-strong:text-brand-deep prose-strong:font-semibold
                        prose-li:text-gray-600 prose-li:text-sm
                        prose-blockquote:border-l-2 prose-blockquote:border-brand-cream prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-500
                        [&_h1]:text-2xl [&_h1]:md:text-3xl [&_h1]:font-bold [&_h1]:mt-10 [&_h1]:mb-4
                        [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3
                        [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2
                        [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:text-gray-600
                        [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:text-gray-600
                      "
                      dangerouslySetInnerHTML={{ __html: article.content_html }}
                    />
                  ) : (
                    article.content_blocks.map((block, i) => {
                      if (block.type === 'text') {
                        return (
                          <p key={i} className="mb-5 text-sm md:text-base leading-relaxed text-gray-600">
                            {block.value}
                          </p>
                        );
                      }
                      if (block.type === 'heading') {
                        return (
                          <h3 key={i} className="text-lg font-semibold text-brand-deep mt-10 mb-4">
                            {block.value}
                          </h3>
                        );
                      }
                      if (block.type === 'image') {
                        return (
                          <figure key={i} className="my-8">
                            <img
                              src={block.value}
                              alt={block.alt || ''}
                              className="w-full object-cover max-h-[500px] object-center"
                            />
                            {block.alt && (
                              <figcaption className="text-xs text-gray-400 text-center mt-3">
                                {block.alt}
                              </figcaption>
                            )}
                          </figure>
                        );
                      }
                      return null;
                    })
                  )}
                </div>

                <div className="mt-10 pt-6 border-t border-brand-light/30 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-light text-brand-deep">
                      <i className="ri-newspaper-line text-sm" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{categoryLabels[article.category] || article.category}</p>
                      <p className="text-xs text-gray-300">{article.date}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/news')}
                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-brand-brown border border-brand-cream rounded-full hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看更多资讯
                    <i className="ri-arrow-right-line text-sm" />
                  </button>
                </div>
              </article>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>
  );
}